import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { AbstractControl, FormGroup, UntypedFormBuilder, ValidationErrors, Validators } from '@angular/forms';
import { SelectionItem } from '../../../core/component/select/select.component';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import {
  VismaRorAccountPartCharacterKind,
  VismaRorAccountPartConfiguration,
  VismaRorAccountPartSourceKind,
  VismaRorExportConfiguration,
} from '../../model/company';

@Component({
  selector: 'app-company-visma-ror-config',
  templateUrl: './company-visma-ror-config.component.html',
  styleUrls: ['./company-visma-ror-config.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CompanyVismaRorConfigComponent implements OnInit {
  constructor(private readonly _cf: FormControlExFactory, private readonly _fb: UntypedFormBuilder) {}

  @Input() dataContext: VismaRorExportConfiguration;
  private _form: FormGroup;

  @Input() set form(value: AbstractControl) {
    this._form = value as FormGroup;
  }

  get form(): FormGroup {
    return this._form;
  }

  readonly accountPartNames = ['konto1', 'konto2', 'konto3', 'konto4', 'konto5', 'konto6', 'konto7', 'konto8', 'konto9', 'konto10'];
  readonly sourceSelection: SelectionItem[] = [
    new SelectionItem('COMPANY.SETTINGS_VISMA_ROR.SOURCE.UNUSED', VismaRorAccountPartSourceKind.Undefined),
    new SelectionItem('COMPANY.SETTINGS_VISMA_ROR.SOURCE.MAIN_ACCOUNT', VismaRorAccountPartSourceKind.MainAccount),
    new SelectionItem('COMPANY.SETTINGS_VISMA_ROR.SOURCE.COST_PLACE', VismaRorAccountPartSourceKind.CostPlace),
    new SelectionItem('COMPANY.SETTINGS_VISMA_ROR.SOURCE.COST_BEARER', VismaRorAccountPartSourceKind.CostBearer),
    new SelectionItem('COMPANY.SETTINGS_VISMA_ROR.SOURCE.PROJECT', VismaRorAccountPartSourceKind.Project),
    new SelectionItem('COMPANY.SETTINGS_VISMA_ROR.SOURCE.COUNTERPART', VismaRorAccountPartSourceKind.CounterPart),
    new SelectionItem('COMPANY.SETTINGS_VISMA_ROR.SOURCE.FUNCTION', VismaRorAccountPartSourceKind.Function),
    new SelectionItem('COMPANY.SETTINGS_VISMA_ROR.SOURCE.FIXED_VALUE', VismaRorAccountPartSourceKind.FixedValue),
  ];
  readonly characterTypeSelection: SelectionItem[] = [
    new SelectionItem('COMPANY.SETTINGS_VISMA_ROR.CHARACTER_TYPE.NUMERIC', VismaRorAccountPartCharacterKind.Numeric),
    new SelectionItem('COMPANY.SETTINGS_VISMA_ROR.CHARACTER_TYPE.ALPHANUMERIC', VismaRorAccountPartCharacterKind.AlphaNumeric),
  ];

  ngOnInit(): void {
    if (this.form.contains('externalCompany')) return;

    this.form.addControl(
      'externalCompany',
      this._cf.createWithOptions('COMPANY.SETTINGS_VISMA_ROR.EXTERNAL_COMPANY', this.dataContext.externalCompany, {
        length: 5,
        required: true,
        validator: Validators.pattern(/^\d{5}$/),
      })
    );
    this.form.addControl(
      'internalCompany',
      this._cf.createWithOptions('COMPANY.SETTINGS_VISMA_ROR.INTERNAL_COMPANY', this.dataContext.internalCompany, {
        length: 10,
        validator: Validators.pattern(/^\d*$/),
      })
    );
    this.form.addControl(
      'senderRoutine',
      this._cf.createWithOptions('COMPANY.SETTINGS_VISMA_ROR.SENDER_ROUTINE', this.dataContext.senderRoutine, {
        length: 3,
        required: true,
      })
    );
    this.form.addControl(
      'senderSubroutine',
      this._cf.createWithOptions('COMPANY.SETTINGS_VISMA_ROR.SENDER_SUBROUTINE', this.dataContext.senderSubroutine, { length: 3 })
    );
    this.form.addControl(
      'originType',
      this._cf.createWithOptions('COMPANY.SETTINGS_VISMA_ROR.ORIGIN_TYPE', this.dataContext.originType, {
        length: 3,
        required: true,
        validator: Validators.pattern(/^\d{3}$/),
      })
    );
    this.form.addControl(
      'processingType',
      this._cf.createWithOptions('COMPANY.SETTINGS_VISMA_ROR.PROCESSING_TYPE', this.dataContext.processingType, {
        length: 3,
        required: true,
        validator: Validators.pattern(/^\d{3}$/),
      })
    );
    this.form.addControl(
      'fileExtension',
      this._cf.createWithOptions('COMMON.FILE_EXTENSION', this.dataContext.fileExtension, {
        length: 10,
        required: true,
        validator: Validators.pattern(/^[A-Za-z0-9]+$/),
      })
    );
    this.form.addControl(
      'includeTodaysWork',
      this._cf.create('COMPANY.SETTINGS_GL07_FIELDS.INCLUDE_TODAYS_WORK', this.dataContext.includeTodaysWork)
    );
    this.form.addControl(
      'asymmetricBundling',
      this._cf.create(
        'COMPANY.SETTINGS_GL07_FIELDS.ASYMMETRIC_BUNDLING',
        this.dataContext.asymmetricBundling,
        null,
        null,
        'COMPANY.SETTINGS_GL07_FIELDS.HELP_TEXT.ASYMMETRIC_BUNDLING'
      )
    );

    for (const name of this.accountPartNames) {
      const configuration = (this.dataContext as unknown as Record<string, VismaRorAccountPartConfiguration>)[name];
      this.form.addControl(name, this.createAccountPartGroup(configuration));
    }

    this.form.addValidators(control => this.validateMainAccount(control));
  }

  getAccountPart(name: string): FormGroup {
    return this.form.controls[name] as FormGroup;
  }

  isFixedValue(name: string): boolean {
    return this.getAccountPart(name).controls['source'].value === VismaRorAccountPartSourceKind.FixedValue;
  }

  private createAccountPartGroup(configuration: VismaRorAccountPartConfiguration): FormGroup {
    const value = configuration ?? new VismaRorAccountPartConfiguration();
    return this._fb.group({
      source: this._cf.create('COMPANY.SETTINGS_VISMA_ROR.ACCOUNT_PART_SOURCE', value.source),
      fixedValue: this._cf.createWithOptions('COMPANY.SETTINGS_VISMA_ROR.FIXED_VALUE', value.fixedValue, { length: 10 }),
      declaredLength: this._cf.createWithOptions('COMPANY.SETTINGS_VISMA_ROR.DECLARED_LENGTH', value.declaredLength, {
        minValue: 1,
        maxValue: 10,
        required: true,
      }),
      characterType: this._cf.create('COMPANY.SETTINGS_VISMA_ROR.CHARACTER_TYPE', value.characterType),
    });
  }

  private validateMainAccount(control: AbstractControl): ValidationErrors | null {
    const mainAccountCount = this.accountPartNames.filter(
      name => control.get(name)?.get('source')?.value === VismaRorAccountPartSourceKind.MainAccount
    ).length;
    return mainAccountCount === 1
      ? null
      : { vismaRorMainAccount: { message: 'COMPANY.SETTINGS_VISMA_ROR.EXACTLY_ONE_MAIN_ACCOUNT' } };
  }
}
